import type { Routes } from '@angular/router';
import { authGuardFn } from '@auth0/auth0-angular';
import {
    authContextHealthGuard,
    onboardingGuard,
    userLoadedGuard,
} from '@quipex/core/auth';
import { provideActivity } from '@quipex/feature/app-activity';
import {
    appQuippyRoutes,
    provideAppQuippyRuntime,
} from '@quipex/feature/app-quippy';
import { provideRecentBuildings } from '@quipex/feature/building-context';
import { provideDefaultBuildingCandidates } from '@quipex/feature/buildings-find/default-building-candidates';
import { validate as validateUuid } from 'uuid';
import { AppLayout } from './app-layout';
import { provideAppTopBarRollout } from './app-top-bar-rollout';
import { provideHomeSuggestedActions } from './home-suggested-actions';
import { provideBuildingCreationIntent } from './provide-building-creation-intent';
import { AppQuippyScopePickerAdapter } from './quippy-scope-picker-adapter';

export const appRoutes: Routes = [
    {
        path: 'billing/success',
        pathMatch: 'full',
        redirectTo: ({ queryParams }) => {
            const buildingId: unknown = queryParams['buildingId'];

            return typeof buildingId === 'string' && validateUuid(buildingId)
                ? `/buildings/${buildingId.toLowerCase()}`
                : '/buildings';
        },
    },
    {
        path: '',
        title: 'Quipex',
        canActivate: [authGuardFn, userLoadedGuard],
        canActivateChild: [authContextHealthGuard],
        runGuardsAndResolvers: 'always',
        component: AppLayout,
        providers: [
            ...provideActivity(),
            ...provideRecentBuildings(),
            ...provideDefaultBuildingCandidates(),
            ...provideAppQuippyRuntime({
                scopePicker: AppQuippyScopePickerAdapter,
            }),
            provideAppTopBarRollout(),
            provideBuildingCreationIntent(),
        ],
        children: [
            {
                path: 'notifications',
                title: 'Notification settings',
                loadComponent: () =>
                    import('@quipex/feature/app-notifications').then(
                        (mod) => mod.Notifications
                    ),
            },
            {
                path: '',
                pathMatch: 'full',
                title: 'Home',
                providers: [provideHomeSuggestedActions()],
                loadComponent: () =>
                    import('@quipex/feature/app-home').then(
                        (mod) => mod.AppHome
                    ),
            },
            {
                path: 'chat',
                title: 'Ask Quippy',
                children: appQuippyRoutes,
            },
            {
                path: 'organisation',
                data: { preload: true },
                loadChildren: () =>
                    import('@quipex/feature/organisations').then(
                        (mod) => mod.organisationsRoutes
                    ),
            },
            {
                path: 'buildings',
                data: { preload: true },
                loadChildren: () =>
                    import('@quipex/feature/buildings').then((mod) =>
                        mod.createBuildingsRoutes()
                    ),
            },
        ],
    },
    {
        path: 'onboarding',
        title: 'Onboarding',
        loadComponent: () =>
            import('@quipex/feature/onboarding').then((m) => m.Onboarding),
        canActivate: [authGuardFn, onboardingGuard],
    },
    {
        path: 'login',
        title: 'Login',
        loadComponent: () =>
            import('@quipex/feature/onboarding').then((comp) => comp.Login),
    },
    {
        path: 'not-found',
        title: 'Not found',
        loadComponent: () =>
            import('@quipex/feature/onboarding').then((comp) => comp.NotFound),
    },
    {
        path: 'service-unavailable',
        title: 'Service unavailable',
        loadComponent: () =>
            import('@quipex/feature/onboarding').then(
                (comp) => comp.ServiceUnavailable
            ),
    },
    { path: '**', redirectTo: 'not-found' },
];
